import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useBreadcrumb } from '@/app/Breadcrumb'
import { ME_CONTEXT_QUERY_KEY } from '@/app/MeContextProvider'
import {
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  PageHeader,
  Textarea,
  useToast,
} from '@/design/components'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import { plural } from '@/lib/format'

import type { Decision, PermissionRow, PermissionUpdate, Scope } from '../api'
import { adminApi, adminKeys } from '../api'
import { SegmentedControl } from '../components/SegmentedControl'

interface DraftValue {
  decision: Decision | null
  scope: Scope | null
}

type Draft = Record<string, DraftValue>

const DECISION_OPTIONS: { value: Decision; label: string }[] = [
  { value: 'yes', label: 'Tak' },
  { value: 'conditional', label: 'Warunkowo' },
  { value: 'no', label: 'Nie' },
]

const SCOPE_OPTIONS: { value: Scope; label: string }[] = [
  { value: 'all', label: 'Wszystkie' },
  { value: 'team', label: 'Zespół' },
  { value: 'own', label: 'Własne' },
]

function draftFromRows(groups: { permissions: PermissionRow[] }[]): Draft {
  const draft: Draft = {}
  for (const group of groups) {
    for (const row of group.permissions) {
      draft[row.key] = { decision: row.decision, scope: row.scope }
    }
  }
  return draft
}

function scopeLabel(scope: Scope | null): string {
  switch (scope) {
    case 'all':
      return 'wszystkie sprawy'
    case 'team':
      return 'sprawy zespołu'
    case 'own':
      return 'sprawy własne'
    default:
      return 'bez dostępu do rejestru'
  }
}

export function RolesPage() {
  useBreadcrumb(['Administracja', 'Role i uprawnienia'])
  const toast = useToast()
  const queryClient = useQueryClient()

  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null)
  const [draft, setDraft] = useState<Draft>({})
  const [newRoleOpen, setNewRoleOpen] = useState(false)

  const rolesQuery = useQuery({ queryKey: adminKeys.roles, queryFn: adminApi.roles })
  const roles = useMemo(() => rolesQuery.data ?? [], [rolesQuery.data])

  useEffect(() => {
    if (selectedRoleId === null && roles.length > 0) setSelectedRoleId(roles[0].id)
  }, [roles, selectedRoleId])

  const matrixQuery = useQuery({
    queryKey: adminKeys.permissions(selectedRoleId ?? 0),
    queryFn: () => adminApi.permissions(selectedRoleId as number),
    enabled: selectedRoleId !== null,
  })

  useEffect(() => {
    if (matrixQuery.data) setDraft(draftFromRows(matrixQuery.data.groups))
  }, [matrixQuery.data])

  const rows = useMemo(
    () => (matrixQuery.data?.groups ?? []).flatMap((group) => group.permissions),
    [matrixQuery.data],
  )

  const dirty = rows.some(
    (row) =>
      draft[row.key] &&
      (draft[row.key].decision !== row.decision || draft[row.key].scope !== row.scope),
  )

  const saveMutation = useMutation({
    mutationFn: (payload: PermissionUpdate[]) =>
      adminApi.savePermissions(selectedRoleId as number, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(adminKeys.permissions(data.role.id), data)
      setDraft(draftFromRows(data.groups))
      void queryClient.invalidateQueries({ queryKey: ME_CONTEXT_QUERY_KEY })
      toast.success('Zapisano uprawnienia', data.role.name)
    },
    onError: (error) =>
      toast.error(
        'Nie zapisano uprawnień',
        error instanceof ApiError ? error.userMessage : undefined,
      ),
  })

  const duplicateMutation = useMutation({
    mutationFn: (roleId: number) => adminApi.duplicateRole(roleId),
    onSuccess: async (role) => {
      await queryClient.invalidateQueries({ queryKey: adminKeys.roles })
      setSelectedRoleId(role.id)
      toast.success('Utworzono kopię roli', role.name)
    },
    onError: (error) =>
      toast.error('Nie udało się zduplikować roli', error instanceof ApiError ? error.userMessage : undefined),
  })

  const createMutation = useMutation({
    mutationFn: (body: { name: string; description?: string | null }) => adminApi.createRole(body),
    onSuccess: async (role) => {
      await queryClient.invalidateQueries({ queryKey: adminKeys.roles })
      setSelectedRoleId(role.id)
      setNewRoleOpen(false)
      toast.success('Dodano rolę', role.name)
    },
    onError: (error) =>
      toast.error('Nie udało się dodać roli', error instanceof ApiError ? error.userMessage : undefined),
  })

  const setDecision = (row: PermissionRow, decision: Decision) => {
    setDraft((current) => {
      const previous = current[row.key] ?? { decision: null, scope: null }
      const scope =
        row.scoped && decision !== 'no' ? (previous.scope ?? 'own') : row.scoped ? null : null
      return { ...current, [row.key]: { decision, scope } }
    })
  }

  const setScope = (row: PermissionRow, scope: Scope) => {
    setDraft((current) => {
      const previous = current[row.key] ?? { decision: null, scope: null }
      return { ...current, [row.key]: { decision: previous.decision ?? 'yes', scope } }
    })
  }

  const handleSave = () => {
    const payload: PermissionUpdate[] = rows
      .filter((row) => {
        const value = draft[row.key]
        return value && (value.decision !== row.decision || value.scope !== row.scope)
      })
      .map((row) => ({
        key: row.key,
        decision: draft[row.key].decision,
        scope: draft[row.key].scope,
      }))
    if (payload.length === 0) return
    saveMutation.mutate(payload)
  }

  const matrix = matrixQuery.data
  const role = matrix?.role
  const totalUsers = roles.reduce((sum, item) => sum + item.user_count, 0)
  const casesView = draft['cases.view']
  const casesAccess = casesView
    ? `dostęp: ${scopeLabel(casesView.decision === 'no' || casesView.decision === null ? null : casesView.scope)}`
    : null

  return (
    <>
      <PageHeader
        title="Role i uprawnienia"
        subtitle={
          rolesQuery.isPending
            ? 'Wczytywanie…'
            : `${totalUsers} ${plural(totalUsers, 'użytkownik', 'użytkowników', 'użytkowników')} w ${roles.length} ${plural(roles.length, 'roli', 'rolach', 'rolach')}. Role są płaskie — nic nie dziedziczy się między nimi; użytkownik z kilkoma rolami dostaje sumę ich uprawnień.`
        }
        actions={
          <Button variant="primary" onClick={() => setNewRoleOpen(true)}>
            Nowa rola
          </Button>
        }
      />

      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[300px_minmax(0,1fr)]">
        <Card>
          <div className="t-label border-b border-line-soft px-4 py-3">Role</div>
          <ul>
            {roles.map((item) => {
              const active = item.id === selectedRoleId
              return (
                <li key={item.id} className="border-b border-line-soft last:border-b-0">
                  <button
                    type="button"
                    onClick={() => setSelectedRoleId(item.id)}
                    className={cn(
                      'w-full border-l-[3px] px-4 py-3 text-left transition-colors duration-fast',
                      active
                        ? 'border-l-gold bg-[#f7f9fb]'
                        : 'border-l-transparent hover:bg-rowhover',
                    )}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className={cn('text-base', active ? 'font-semibold text-ink' : 'font-medium text-ink')}>
                        {item.name}
                      </span>
                      <span className="t-small shrink-0 text-muted">{item.user_count} os.</span>
                    </div>
                    <p className="t-small mt-0.5 text-muted">{item.description ?? '—'}</p>
                  </button>
                </li>
              )
            })}
          </ul>
        </Card>

        <Card>
          {matrixQuery.isPending || !matrix || !role ? (
            <EmptyState title="Wczytywanie macierzy uprawnień…" />
          ) : (
            <>
              <header className="flex flex-wrap items-start justify-between gap-4 border-b border-line-soft px-5 py-4">
                <div className="min-w-0">
                  <h2 className="t-h2">{role.name}</h2>
                  <p className="t-small mt-1 text-muted">
                    {[
                      role.description,
                      `${role.user_count} ${plural(role.user_count, 'użytkownik', 'użytkowników', 'użytkowników')}`,
                      casesAccess,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    variant="secondary"
                    loading={duplicateMutation.isPending}
                    onClick={() => duplicateMutation.mutate(role.id)}
                  >
                    Duplikuj
                  </Button>
                  <Button
                    variant="primary"
                    disabled={!dirty}
                    loading={saveMutation.isPending}
                    onClick={handleSave}
                  >
                    Zapisz
                  </Button>
                </div>
              </header>

              {matrix.groups.length === 0 ? (
                <EmptyState
                  title="Brak uprawnień do skonfigurowania"
                  description="Żaden włączony moduł nie zadeklarował uprawnień."
                />
              ) : (
                matrix.groups.map((group) => (
                  <section key={group.group}>
                    <h3 className="t-label border-y border-line-soft bg-[#f8fafb] px-5 py-2.5">
                      {group.group}
                    </h3>
                    <ul>
                      {group.permissions.map((row) => {
                        const value = draft[row.key] ?? { decision: null, scope: null }
                        return (
                          <li
                            key={row.key}
                            className="flex flex-wrap items-center justify-between gap-4 border-b border-line-soft px-5 py-3.5 last:border-b-0"
                          >
                            <div className="min-w-0">
                              <p className="text-base font-medium text-ink">{row.label}</p>
                              <p className="t-small text-muted">{row.hint}</p>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                              <SegmentedControl<Decision>
                                aria-label={`${row.label} — decyzja`}
                                options={DECISION_OPTIONS.map((option) => ({
                                  ...option,
                                  disabled:
                                    option.value === 'conditional' && !row.conditional_allowed,
                                  title:
                                    option.value === 'conditional' && !row.conditional_allowed
                                      ? 'To uprawnienie nie ma wariantu warunkowego'
                                      : undefined,
                                }))}
                                value={value.decision}
                                onChange={(decision) => setDecision(row, decision)}
                              />
                              {row.scoped ? (
                                <SegmentedControl<Scope>
                                  aria-label={`${row.label} — zakres`}
                                  options={SCOPE_OPTIONS}
                                  value={value.scope}
                                  disabled={value.decision === 'no' || value.decision === null}
                                  onChange={(scope) => setScope(row, scope)}
                                />
                              ) : null}
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                  </section>
                ))
              )}
            </>
          )}
        </Card>
      </div>

      <NewRoleModal
        open={newRoleOpen}
        pending={createMutation.isPending}
        onClose={() => setNewRoleOpen(false)}
        onSubmit={(body) => createMutation.mutate(body)}
      />
    </>
  )
}

interface NewRoleModalProps {
  open: boolean
  pending: boolean
  onClose: () => void
  onSubmit: (body: { name: string; description?: string | null }) => void
}

function NewRoleModal({ open, pending, onClose, onSubmit }: NewRoleModalProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  useEffect(() => {
    if (open) {
      setName('')
      setDescription('')
    }
  }, [open])

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nowa rola"
      subtitle="Rola powstaje bez uprawnień — ustawisz je w macierzy."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            variant="primary"
            loading={pending}
            disabled={name.trim().length < 2}
            onClick={() => onSubmit({ name: name.trim(), description: description.trim() || null })}
          >
            Utwórz rolę
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Nazwa roli"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="np. Of Counsel"
          autoFocus
        />
        <Textarea
          label="Opis"
          rows={3}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Krótki opis zakresu obowiązków"
        />
      </div>
    </Modal>
  )
}
